import type { Logger } from "@expert/logging";
import type { EligibleProduct } from "@expert/sales-tools";

/** Session context keys that Gaia accepts for realtime_tools. Only these values are sent. */
const ALLOWED_SESSION_CONTEXT_KEYS = new Set([
    "gaia-engagement-question-checklist",
    "gaia-example-of-coverage",
    "gaia-sales-checklist-mentions",
    "gaia-enrollment-celebration-checklist",
]);

/**
 * Extracts all unique session context keys from each product's salesOfferChecklist
 * (optional comma-separated string) across the given products and locales.
 * Only primary products (isPrimaryProduct === true) are considered.
 * Only keys in ALLOWED_SESSION_CONTEXT_KEYS are included.
 * Returns an array of strings suitable for sending to Gaia as realtime_tools.
 */
export function getSessionContextKeysFromProducts(logger: Logger, products: EligibleProduct[]): string[] {
    const keys = new Set<string>();
    for (const product of products) {
        if (!product.isPrimaryProduct) continue;
        const productInfoByLocale = product.productInfo;
        if (!productInfoByLocale) continue;
        for (const productInfo of Object.values(productInfoByLocale)) {
            const csv = productInfo?.salesOfferChecklist?.sessionContextKeys;
            if (!csv?.trim()) continue;
            for (const key of csv
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)) {
                if (ALLOWED_SESSION_CONTEXT_KEYS.has(key)) {
                    keys.add(key);
                } else {
                    logger.warn({ key }, "Skipping session context key as it is not allowed");
                }
            }
        }
    }
    return Array.from(keys);
}
